/**
 * Image Upload Service
 * 
 * Handles image file uploads to backend via FormData.
 * Provides validation, upload progress tracking, and error handling.
 * 
 * Based on admin panel's productImage.service.ts but adapted for JavaScript.
 */

import apiClient from './apiClient';
import { getAccessToken } from '../utils/tokenManager';
import { getApiBaseUrl } from '../config/api.config';

/**
 * @typedef {Object} ImageUploadResult
 * @property {boolean} success - Upload success status
 * @property {string} imageId - Uploaded image ID
 * @property {string} url - Public URL of uploaded image
 * @property {string} fileName - Original file name
 * @property {number} fileSize - File size in bytes
 * @property {string} [error] - Error message if upload failed
 */

/**
 * @typedef {Object} ImageValidationResult
 * @property {boolean} isValid - Validation status
 * @property {string} [error] - Error message if invalid
 */

/**
 * Validates image file (type, size, format)
 * @param {File} file - File to validate
 * @returns {ImageValidationResult} Validation result
 */
export const validateImageFile = (file) => {
  // Check if file exists
  if (!file) {
    return {
      isValid: false,
      error: 'No file provided'
    };
  }

  // File size validation (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: 'File size exceeds 10MB limit'
    };
  }

  // File type validation
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
  if (!allowedTypes.includes(file.type)) {
    return {
      isValid: false,
      error: 'File type not supported. Please use JPEG, PNG, WebP, or GIF'
    };
  }

  return { isValid: true };
};

/**
 * Uploads single image file to backend
 * @param {File} file - Image file from input
 * @param {number} productId - Product ID to associate image with
 * @returns {Promise<ImageUploadResult>} Upload result with URL
 */
export const uploadProductImage = async (file, productId) => {
  try {
    // Validate file first
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      return {
        success: false,
        imageId: '',
        url: '',
        fileName: file.name,
        fileSize: file.size,
        error: validation.error
      };
    }

    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append('image', file);
    formData.append('productId', productId.toString());

    // Get authentication token
    const token = getAccessToken();
    if (!token) {
      throw new Error('Authentication required');
    }

    // Get API base URL from centralized config (same source as apiClient.js)
    const apiBaseUrl = getApiBaseUrl();
    const uploadUrl = `${apiBaseUrl}/images/upload`;

    // Upload to backend using fetch (to avoid axios FormData issues)
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Upload failed with status ${response.status}`);
    }

    const data = await response.json();

    return {
      success: true,
      imageId: data.data?.productId?.toString() || data.data?.id?.toString() || '',
      url: data.data?.imageUrl || data.data?.url || '',
      fileName: file.name,
      fileSize: file.size,
    };
  } catch (error) {
    console.error('Image upload error:', error);
    return {
      success: false,
      imageId: '',
      url: '',
      fileName: file.name,
      fileSize: file.size,
      error: error.message || 'Failed to upload image'
    };
  }
};

/**
 * Uploads multiple images with progress tracking
 * @param {File[]} files - Array of image files
 * @param {number} productId - Product ID
 * @param {Function} onProgress - Progress callback (fileIndex, progress, status)
 * @returns {Promise<ImageUploadResult[]>} Array of upload results
 */
export const uploadMultipleImages = async (files, productId, onProgress = null) => {
  const results = [];
  
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    
    // Notify progress start
    if (onProgress) {
      onProgress(i, 0, 'uploading', file.name);
    }
    
    try {
      const result = await uploadProductImage(file, productId);
      results.push(result);
      
      // Notify progress complete
      if (onProgress) {
        onProgress(i, 100, result.success ? 'completed' : 'error', file.name, result);
      }
    } catch (error) {
      const errorResult = {
        success: false,
        imageId: '',
        url: '',
        fileName: file.name,
        fileSize: file.size,
        error: error.message || 'Upload failed'
      };
      results.push(errorResult);
      
      // Notify progress error
      if (onProgress) {
        onProgress(i, 0, 'error', file.name, errorResult);
      }
    }
  }
  
  return results;
};

/**
 * Deletes image from backend
 * @param {string} imageId - Image ID to delete
 * @returns {Promise<void>}
 */
export const deleteProductImage = async (imageId) => {
  try {
    await apiClient.delete(`/images/${imageId}`);
  } catch (error) {
    console.error('Image delete error:', error);
    throw new Error(error.response?.data?.message || 'Failed to delete image');
  }
};

/**
 * Creates preview URL for file (client-side utility)
 * @param {File} file - File to create preview for
 * @returns {string} Object URL for preview
 */
export const createPreviewUrl = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Cleanup preview URL (client-side utility)
 * @param {string} url - Object URL to revoke
 */
export const cleanupPreviewUrl = (url) => {
  URL.revokeObjectURL(url);
};

/**
 * Formats file size for display (e.g., "2.5 MB")
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Image Upload Service Object
 */
const imageUploadService = {
  validateImageFile,
  uploadProductImage,
  uploadMultipleImages,
  deleteProductImage,
  createPreviewUrl,
  cleanupPreviewUrl,
  formatFileSize,
};

export default imageUploadService;
